import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    IconButton,
    TableRow,
    TableCell,
    tableCellClasses,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Switch,
    FormControlLabel,
    InputAdornment,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { showLoader } from '../../Redux/Slices/loaderSlice';
import {
    getExpenseCategories,
    createExpenseCategory,
    updateExpenseCategory,
    deleteExpenseCategory,
    seedExpenseCategories,
} from '../../Redux/Actions/action';
import ErrorHandler from '../../Utils/handler';
import { toast } from 'react-toastify';
import CommonTable from '../../Components/Common/CommonTable';
import DataNotFound from '../../Components/Common/DataNotFound';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        color: theme?.palette?.bgLightWhite?.main,
        fontSize: 14,
        textAlign: 'center',
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        textAlign: 'center',
        padding: '12px 8px',
    },
}));

const defaultFormData = {
    name: '',
    description: '',
    icon: '📝',
    color: '#3E4094',
    budgetLimit: '',
    sortOrder: 0,
    isActive: true,
};

const ExpenseCategories = () => {
    const dispatch = useDispatch();

    const [categories, setCategories] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [formData, setFormData] = useState(defaultFormData);
    const [errors, setErrors] = useState({});

    const headerList = ['No', 'Icon', 'Name', 'Description', 'Budget Limit', 'Expenses', 'Total Amount', 'Status', 'Actions'];

    const fetchCategories = async () => {
        dispatch(showLoader(true));
        try {
            const result = await dispatch(getExpenseCategories({ includeInactive: true }));
            if (result?.payload?.success) {
                setCategories(result?.payload?.data || []);
            }
        } catch (error) {
            console.error('Fetch categories error:', error);
        } finally {
            dispatch(showLoader(false));
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    const handleOpenDialog = (category = null) => {
        if (category) {
            setSelectedCategory(category);
            setFormData({
                name: category.name || '',
                description: category.description || '',
                icon: category.icon || '📝',
                color: category.color || '#3E4094',
                budgetLimit: category.budgetLimit || '',
                sortOrder: category.sortOrder || 0,
                isActive: category.isActive !== false,
            });
        } else {
            setSelectedCategory(null);
            setFormData(defaultFormData);
        }
        setErrors({});
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedCategory(null);
        setFormData(defaultFormData);
    };

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: '' }));
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Name is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        dispatch(showLoader(true));
        try {
            const payload = {
                ...formData,
                budgetLimit: formData.budgetLimit ? parseFloat(formData.budgetLimit) : 0,
            };

            if (selectedCategory) {
                payload.id = selectedCategory._id;
            }

            const action = selectedCategory ? updateExpenseCategory : createExpenseCategory;
            const result = await dispatch(action(payload));

            if (ErrorHandler(result)) {
                toast.success(selectedCategory ? 'Category updated' : 'Category created');
                handleCloseDialog();
                fetchCategories();
            }
        } catch (error) {
            toast.error('Failed to save category');
        } finally {
            dispatch(showLoader(false));
        }
    };

    const handleDelete = async () => {
        dispatch(showLoader(true));
        try {
            const result = await dispatch(deleteExpenseCategory(selectedCategory._id));
            if (ErrorHandler(result)) {
                toast.success('Category deleted');
                setDeleteDialogOpen(false);
                setSelectedCategory(null);
                fetchCategories();
            }
        } catch (error) {
            toast.error('Failed to delete category');
        } finally {
            dispatch(showLoader(false));
        }
    };

    const handleSeedCategories = async () => {
        dispatch(showLoader(true));
        try {
            const result = await dispatch(seedExpenseCategories());
            if (ErrorHandler(result)) {
                toast.success(result?.payload?.message || 'Default categories added');
                fetchCategories();
            }
        } catch (error) {
            toast.error('Failed to seed categories');
        } finally {
            dispatch(showLoader(false));
        }
    };

    const emojiList = ['📦', '⚡', '🏢', '🚗', '💰', '🍽️', '💻', '📢', '👔', '📝', '🛠️', '📞', '🏥', '📚', '🎯'];

    return (
        <Box>
            <Paper elevation={0} sx={{ borderRadius: '24px', padding: '24px' }}>
                {/* Header */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h5" fontWeight={600}>
                        Expense Categories
                    </Typography>
                    <Box display="flex" gap={1}>
                        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleSeedCategories}>
                            Add Default Categories
                        </Button>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                            Add Category
                        </Button>
                    </Box>
                </Box>

                {/* Table */}
                <CommonTable headerList={headerList} minWidth={900}>
                    {categories.length > 0 ? (
                        categories.map((cat, index) => (
                            <TableRow key={cat._id} hover>
                                <StyledTableCell>{index + 1}</StyledTableCell>
                                <StyledTableCell>
                                    <Box
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: 2,
                                            backgroundColor: cat.color + '20',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            margin: 'auto',
                                        }}
                                    >
                                        <Typography fontSize={20}>{cat.icon}</Typography>
                                    </Box>
                                </StyledTableCell>
                                <StyledTableCell sx={{ fontWeight: 600 }}>{cat.name}</StyledTableCell>
                                <StyledTableCell sx={{ maxWidth: 200 }}>
                                    {cat.description || '-'}
                                </StyledTableCell>
                                <StyledTableCell>
                                    {cat.budgetLimit ? `₹${cat.budgetLimit.toLocaleString()}` : 'No Limit'}
                                </StyledTableCell>
                                <StyledTableCell>{cat.expenseCount || 0}</StyledTableCell>
                                <StyledTableCell sx={{ fontWeight: 600, color: cat.color }}>
                                    ₹{cat.totalAmount?.toLocaleString() || 0}
                                </StyledTableCell>
                                <StyledTableCell>
                                    <Box
                                        sx={{
                                            px: 1.5,
                                            py: 0.5,
                                            borderRadius: 1,
                                            bgcolor: cat.isActive ? '#e8f5e9' : '#ffebee',
                                            color: cat.isActive ? '#2e7d32' : '#c62828',
                                            display: 'inline-block',
                                            fontSize: 12,
                                            fontWeight: 600,
                                        }}
                                    >
                                        {cat.isActive ? 'Active' : 'Inactive'}
                                    </Box>
                                </StyledTableCell>
                                <StyledTableCell>
                                    <IconButton size="small" onClick={() => handleOpenDialog(cat)}>
                                        <EditIcon fontSize="small" color="primary" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            setSelectedCategory(cat);
                                            setDeleteDialogOpen(true);
                                        }}
                                        disabled={cat.expenseCount > 0}
                                    >
                                        <DeleteIcon fontSize="small" color={cat.expenseCount > 0 ? 'disabled' : 'error'} />
                                    </IconButton>
                                </StyledTableCell>
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <StyledTableCell colSpan={headerList.length}>
                                <DataNotFound
                                    title="No Categories Found"
                                    icon={<ErrorOutlineIcon color="primary" style={{ fontSize: '3rem' }} />}
                                    elevation={0}
                                />
                            </StyledTableCell>
                        </TableRow>
                    )}
                </CommonTable>
            </Paper>

            {/* Add/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{selectedCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Name *"
                                value={formData.name}
                                onChange={(e) => handleChange('name', e.target.value)}
                                error={Boolean(errors.name)}
                                helperText={errors.name}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description"
                                value={formData.description}
                                onChange={(e) => handleChange('description', e.target.value)}
                                multiline
                                rows={2}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" mb={1}>Icon</Typography>
                            <Box display="flex" gap={0.5} flexWrap="wrap">
                                {emojiList.map((emoji) => (
                                    <Box
                                        key={emoji}
                                        onClick={() => handleChange('icon', emoji)}
                                        sx={{
                                            width: 36,
                                            height: 36,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: 1,
                                            cursor: 'pointer',
                                            border: formData.icon === emoji ? '2px solid #3E4094' : '1px solid #ddd',
                                            bgcolor: formData.icon === emoji ? '#3E409410' : 'transparent',
                                        }}
                                    >
                                        {emoji}
                                    </Box>
                                ))}
                            </Box>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                type="color"
                                label="Color"
                                value={formData.color}
                                onChange={(e) => handleChange('color', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Monthly Budget Limit"
                                value={formData.budgetLimit}
                                onChange={(e) => handleChange('budgetLimit', e.target.value)}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                }}
                                helperText="Leave empty for no limit"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Sort Order"
                                value={formData.sortOrder}
                                onChange={(e) => handleChange('sortOrder', parseInt(e.target.value) || 0)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isActive}
                                        onChange={(e) => handleChange('isActive', e.target.checked)}
                                    />
                                }
                                label="Active"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained">
                        {selectedCategory ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Category</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete "{selectedCategory?.name}"?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ExpenseCategories;
